import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useAlert } from 'react-alert';
import { useMutation } from '@apollo/client';
import { errorMessage } from '@firsttable/functions';
import { CREATE_OAUTH_MEMBER, updateLiveUser } from '@firsttable/graphql-queries/src';
import type { FormField } from '@firsttable/types';
import withAnalytics from '@components/analytics/hocs/withAnalytics';
import Logger from '@/lib/logger';
import { resolveAuthMember, ssoEvent } from '@/lib/events';
import { NEXT_PUBLIC_LOGIN_METHODS } from '@/lib/env';
import { useSuburbPreferenceSignupFields } from '@/components/analytics/hooks/useSuburbPreferenceSignupStep';
import { applySignupAttribution, useSignupAttribution } from '@context/SignupAttribution';
import { reportErrorToNewRelic } from '@/lib/newrelic/reportError';
import { AUTH_COMPLETION_ERROR_MESSAGE } from '@/components/context/FormContext/constants';
import { normaliseContactPermission } from '@/lib/analytics/contactPermission';
import OAuthButton from './oAuthButton';
import type { AuthContextProps } from '../../context/AuthContext';
import { FormContext } from '../../context/FormContext/FormContext';

export interface IOAuthResponse {
  method?: string;
  variables?: any;
}
export interface IOAuthProvider {
  trigger: () => void;
  name?: string;
  provider: {
    context?: any;
    ready: boolean;
    failed: boolean;
    loading: boolean;
    disabled: boolean;
  };
  Icon?: React.FC;
}

export type OAuthProviderSignature = {
  method: string;
  logger: Logger;
  setActive: React.Dispatch<React.SetStateAction<boolean>>;
  setError: (message?: string | boolean) => void;
  setResponse: React.Dispatch<React.SetStateAction<IOAuthResponse>>;
};

type OAuthCompletionProps = {
  setFormStatus?: (status: { message: string; type: 'danger' }) => void;
  values?: unknown;
};

export type OAuthSignupValues = {
  cityId?: number;
  // createOAuthMember cannot store this. Incomplete-account completion submits it via updateLiveUser.
  suburbIds?: number[];
  promoCode?: string;
  contactPermission?: boolean;
  termsAndPrivacy?: boolean;
};

export type OAuthWrapperProps = {
  method: string;
  useProvider: (props: OAuthProviderSignature) => IOAuthProvider;
  handleLogin: AuthContextProps['handleLogin'];
  track: (eventName: string, data?: any) => void;
  identify: (userId: string, data?: any) => void;
  attributionPromoCode?: string | null;
  transformCompletionFields?: (fields: FormField[]) => FormField[];
  redirectURL?: string;
  redirectToMemberCity?: boolean;
  onComplete?: (auth: { action?: string }) => void;
  expanded?: boolean;
  boldLabel?: boolean;
  signupValues?: OAuthSignupValues;
};

export const useFireError = (defaultMessage: string) => {
  const [error, setError] = useState<string | null | boolean>(null);
  const alert = useAlert();

  useEffect(() => {
    if (error) {
      alert.error(typeof error === 'string' ? error : defaultMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const fireError = (message: string | boolean = true) => {
    setError(message || true);
  };
  return fireError;
};

const DefaultContextProvider = ({ children }: { children: React.ReactNode }) => <>{children}</>;

const OAuthWrapper = ({
  method,
  useProvider,
  handleLogin,
  track,
  identify,
  attributionPromoCode,
  transformCompletionFields,
  redirectURL,
  redirectToMemberCity = false,
  onComplete,
  expanded,
  boldLabel,
  signupValues,
}: OAuthWrapperProps) => {
  const logger = new Logger(`${method} login`);
  const [active, setActive] = useState(false);
  const fireError = useFireError(`Unable to login with ${method}`);
  const [response, setResponse] = useState<IOAuthResponse>({});
  const { state, update, reset } = useContext(FormContext);
  const signupAttribution = useSignupAttribution();
  const extraInitialValuesRef = useRef(state?.extraInitialValues);
  extraInitialValuesRef.current = state?.extraInitialValues;
  const signupValuesRef = useRef(signupValues);
  signupValuesRef.current = signupValues;
  const {
    provider,
    trigger,
    name: serviceName,
    Icon,
  } = useProvider({
    method,
    logger,
    setActive,
    setError: fireError,
    setResponse,
  });

  const name = serviceName ?? (method && method[0].toUpperCase() + method.substring(1));

  const [createMember, { error }] = useMutation(CREATE_OAUTH_MEMBER);
  const [member, setMember] = useState<any>(null);
  const backendFormFields = useSuburbPreferenceSignupFields(member?.formFields);
  const formFields = useMemo(
    () => transformCompletionFields?.(backendFormFields) ?? backendFormFields,
    [backendFormFields, transformCompletionFields]
  );
  const router = useRouter();
  const btnRef = useRef(null);

  const ContextProvider = provider?.context ?? DefaultContextProvider;

  // Use url to trigger login
  useEffect(() => {
    const { ready, disabled } = provider;
    if (ready && !disabled && router?.query?.trigger === method) {
      // btnRef.current.click();
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, provider]);

  useEffect(() => {
    const login = async () => {
      setActive(true);
      const extras = {
        ...extraInitialValuesRef.current,
        ...signupValuesRef.current,
      };
      const cityId = extras.cityId == null ? undefined : Number(extras.cityId);
      try {
        const {
          data: { oAuthMember },
        } = await createMember({
          variables: applySignupAttribution(
            {
              ...response.variables,
              siteId: +process.env.NEXT_PUBLIC_SITE_ID,
              cityId: Number.isFinite(cityId) ? cityId : undefined,
              promoCode: extras.promoCode || undefined,
              contactPermission: normaliseContactPermission(extras.contactPermission),
              termsAndPrivacy: extras.termsAndPrivacy,
            },
            signupAttribution,
            { surface: `oauth signup (${method})`, logWhenEmpty: true }
          ),
        });

        if (oAuthMember) {
          setMember(oAuthMember);
        } else {
          if (error) {
            logger.debug('Exception', errorMessage(error));
          }
          setActive(false);
          fireError();
        }
      } catch (e) {
        logger.debug('Exception', errorMessage(e));
        setActive(false);
        fireError();
      }
    };

    if (response?.variables) {
      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (NEXT_PUBLIC_LOGIN_METHODS.includes('validate-member')) {
      if (active) {
        update({
          active,
          method,
          mutation: updateLiveUser,
          actionLabel: 'Update Details',
          extraInitialValues: {
            ...extraInitialValuesRef.current,
            ...signupValuesRef.current,
            siteId: +process.env.NEXT_PUBLIC_SITE_ID,
          },
          complete: (updatedMember, { setFormStatus, values }: OAuthCompletionProps = {}) => {
            const completedMember = resolveAuthMember(updatedMember);

            if (!completedMember?.id) {
              reportErrorToNewRelic(
                new Error('OAuth completion response did not contain a member ID'),
                { authMethod: method }
              );
              if (setFormStatus) {
                setFormStatus({ message: AUTH_COMPLETION_ERROR_MESSAGE, type: 'danger' });
              } else {
                fireError(AUTH_COMPLETION_ERROR_MESSAGE);
              }
              throw new Error(AUTH_COMPLETION_ERROR_MESSAGE);
            }

            const currentMember = resolveAuthMember(member) ?? {};
            const completedAuth = {
              ...member,
              member: {
                ...currentMember,
                ...completedMember,
              },
            };

            ssoEvent(name, completedAuth, logger, track, identify, {
              values,
              ...(attributionPromoCode !== undefined ? { attributionPromoCode } : {}),
            });
            // Only flows that own the post-auth destination opt in here; everything else leaves the redirect to AuthContext so backUrl/redirectPath return the diner to their action.
            const destination =
              redirectURL ||
              (redirectToMemberCity
                ? completedMember.city?.slug || currentMember.city?.slug
                : undefined);
            const result = handleLogin({
              ...completedAuth,
              ...(destination ? { redirect: false } : {}),
            });
            if (destination) {
              router.push(destination).catch((redirectError) => {
                logger.debug('Redirect failed', errorMessage(redirectError));
              });
            }
            onComplete?.(completedAuth);
            return result;
          },
          cancel: () => {
            setMember(null);
            setActive(false);
          },
          formFields,
          member: {
            ...member,
          },
          token: member?.token,
          cdnToken: member?.cdnToken,
        });
      } else {
        reset({
          extraInitialValues: extraInitialValuesRef.current,
        });
      }
    } else if (member) {
      // Old method
      const destination =
        redirectURL || (redirectToMemberCity ? resolveAuthMember(member)?.city?.slug : undefined);
      const completedAuth = {
        ...member,
        ...(destination ? { redirect: destination } : {}),
      };
      ssoEvent(name, completedAuth, logger, track, identify, {
        ...(attributionPromoCode !== undefined ? { attributionPromoCode } : {}),
      });
      handleLogin(completedAuth);
      onComplete?.(completedAuth);
    }
    // OAuth completion is a state-machine transition; callback identities must not retrigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, member, formFields]);

  return (
    <ContextProvider>
      <OAuthButton
        ref={btnRef}
        loading={provider.loading}
        disabled={provider.disabled}
        active={active}
        complete={!!member}
        unavailable={provider.failed}
        onClick={trigger}
        method={name}
        Icon={Icon}
        expanded={expanded}
        boldLabel={boldLabel}
      />
    </ContextProvider>
  );
};

export default withAnalytics(OAuthWrapper);
